import React from "react";
import {
  Popover as AriaPopover,
  PopoverProps as AriaPopoverProps,
  composeRenderProps,
  Modal,
  OverlayArrow,
  PopoverContext,
  useSlottedContext,
} from "react-aria-components";
import { ModalBackdrop } from "../ModalBackdrop";

/**
 * Popover
 *
 * Positioned overlay container with optional arrow, built on
 * `react-aria-components` Popover.
 */
export interface PopoverProps extends Omit<AriaPopoverProps, "children"> {
  /**
   * Whether to show an arrow pointing to the trigger
   * @default false
   */
  showArrow?: boolean;
  /**
   * Whether to include a backdrop (makes it modal)
   * @default false
   */
  withBackdrop?: boolean;
  /**
   * Whether the backdrop/modal can be dismissed by clicking outside
   * Only applies when withBackdrop is true
   * @default true
   */
  isDismissable?: boolean;
  /**
   * Whether to disable the group class (prevents group-hover propagation to children)
   * Useful for calendars and other components that use their own group-hover states
   * @default false
   */
  disableGroup?: boolean;
  /**
   * Popover content
   */
  children: React.ReactNode;
}

function getPopoverStyles({
  isEntering,
  isExiting,
  className = "",
  disableGroup = false,
}: {
  isEntering?: boolean;
  isExiting?: boolean;
  className?: string;
  disableGroup?: boolean;
}) {
  const baseStyles = [
    !disableGroup && "group",
    "bg-background-surface",
    "forced-colors:bg-[Canvas]",
    "shadow-lg",
    "rounded-[var(--control-border-radius)]",
    "bg-clip-padding",
    "border",
    "border-border-default",
    "outline-none",
  ]
    .filter(Boolean)
    .join(" ");

  const enteringStyles = isEntering
    ? [
        "animate-in",
        "fade-in",
        "placement-bottom:slide-in-from-top-1",
        "placement-top:slide-in-from-bottom-1",
        "placement-left:slide-in-from-right-1",
        "placement-right:slide-in-from-left-1",
        "ease-out",
        "duration-200",
      ].join(" ")
    : "";

  const exitingStyles = isExiting
    ? [
        "animate-out",
        "fade-out",
        "placement-bottom:slide-out-to-top-1",
        "placement-top:slide-out-to-bottom-1",
        "placement-left:slide-out-to-right-1",
        "placement-right:slide-out-to-left-1",
        "ease-in",
        "duration-150",
      ].join(" ")
    : "";

  return [baseStyles, enteringStyles, exitingStyles, className].filter(Boolean).join(" ");
}

export function Popover({
  children,
  showArrow = false,
  withBackdrop = false,
  className,
  isDismissable = true,
  disableGroup = false,
  ...props
}: PopoverProps) {
  const popoverContext = useSlottedContext(PopoverContext)!;
  const isSubmenu = popoverContext?.trigger === "SubmenuTrigger";
  let offset = showArrow ? 12 : 8;
  offset = isSubmenu ? offset - 6 : offset;

  const popoverContent = (
    <AriaPopover
      offset={offset}
      {...props}
      className={composeRenderProps(className, (className, renderProps) =>
        getPopoverStyles({ ...renderProps, className, disableGroup })
      )}
    >
      {showArrow && (
        <OverlayArrow>
          <svg
            width={12}
            height={12}
            viewBox="0 0 12 12"
            className="block fill-background-surface stroke-border-default stroke-1 group-placement-left:-rotate-90 group-placement-right:rotate-90 group-placement-bottom:rotate-180 forced-colors:fill-[Canvas] forced-colors:stroke-[ButtonBorder]"
          >
            <path d="M0 0 L6 6 L12 0" />
          </svg>
        </OverlayArrow>
      )}
      {children}
    </AriaPopover>
  );

  if (withBackdrop) {
    return (
      <ModalBackdrop isDismissable={isDismissable}>
        <Modal
          className="outline-none focus:outline-none focus-visible:outline-none ring-0 focus:ring-0 focus-visible:ring-0"
          isDismissable={isDismissable}
        >
          {popoverContent}
        </Modal>
      </ModalBackdrop>
    );
  }

  return popoverContent;
}
